import type { ReactNode } from "react";
import { cn } from "@/client/lib/utils";

type NoticeTone = "warning" | "info" | "success" | "error";

const TONE: Record<NoticeTone, { wrap: string; icon: string }> = {
  warning: { wrap: "border-amber-500/40 bg-amber-50/60 text-amber-900", icon: "text-amber-600" },
  info: { wrap: "border-border bg-muted/40 text-foreground", icon: "text-muted-foreground" },
  success: { wrap: "border-emerald-500/40 bg-emerald-50/60 text-emerald-900", icon: "text-emerald-600" },
  error: { wrap: "border-destructive/50 bg-destructive/5 text-destructive-foreground", icon: "text-destructive" },
};

/**
 * Compact inline notice/alert in Origin UI style. The icon is aligned to the first line
 * of the title (not floated above it), padding is tight, and tone drives the colours.
 */
export function Notice({
  tone = "info",
  icon,
  title,
  children,
  action,
  className,
}: {
  tone?: NoticeTone;
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border px-3 py-2.5 text-sm", TONE[tone].wrap, className)}>
      <div className="flex gap-2.5">
        {icon && (
          <span className={cn("mt-px shrink-0 [&_svg]:size-3.5", TONE[tone].icon)} aria-hidden="true">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {title && <p className="font-medium leading-5">{title}</p>}
          {children && <div className="mt-0.5 text-[13px] leading-snug opacity-80">{children}</div>}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </div>
    </div>
  );
}
